import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Sun, Moon, ListTodo, ChevronLeft, Bell, Sparkles } from "lucide-react";
import { useAssistant } from "../pages/assistant/AssistantContext";
import { cleanReportName } from "../utils/Formatter";
import { useTheme } from "../context/ThemeContext";
import { fetchNotificationUnreadCount } from "../apis/notifications";
import { onBadgeRefresh } from "../utils/notificationEvents";
import useAutoRefresh from "../hooks/useAutoRefresh";
import NotificationsPanel from "./NotificationsPanel";
import { MinimizePageButton } from "../context/workspace/WorkspaceContext";
import "../styles/components/Header.css";

const TAB_SUBTITLES = {
  marketing: "Manage all marketing activities and customer interactions",
  engineering: "Manage engineering drawings, BOMs and design data",
  quality: "Track inspections, parameters and quality reports",
  purchase: "Handle purchase orders, vendors and procurement",
  production: "Plan and monitor production, process and route cards",
  accounts: "Manage invoices, payments and financial records",
  maintenance: "Schedule and track maintenance activities",
  store: "Manage inventory, stock balances and material movement",
  master: "Manage customers, vendors, products and reference data",
  rights: "Configure roles, permissions and access control",
  reports: "View business analytics and detailed reports",
  "manual-reports": "Access manually generated reports",
  settings: "Configure menus, visibility and preferences",
  profile: "View and update organisation details",
  tasks: "Organise your to-dos across teams and boards",
};

const Header = () => {
  const assistant = useAssistant();
  const navigate = useNavigate();
  const location = useLocation();
  const { isDark, toggleTheme } = useTheme();
  const [unreadCount, setUnreadCount] = useState(0);
  const [panelOpen, setPanelOpen] = useState(false);
  const bellRef = useRef(null);

  const loadUnreadCount = useCallback(async () => {
    try {
      const res = await fetchNotificationUnreadCount();
      if (typeof res?.count === "number") setUnreadCount(res.count);
    } catch (e) {
    }
  }, []);

  useEffect(() => {
    loadUnreadCount();
  }, [loadUnreadCount, location.pathname]);

  useEffect(() => onBadgeRefresh(loadUnreadCount), [loadUnreadCount]);

  useAutoRefresh(loadUnreadCount);

  const isProfileActive = location.pathname === "/profile";
  const isTasksActive = location.pathname === "/tasks";
  const isHome = location.pathname === "/";

  const formatPath = (path) => {
    if (path === "/") return "Home";
    const selectedReportName = location.state?.selectedReportName;
    if (
      path.includes("/reports/") &&
      path.includes("/details") &&
      selectedReportName
    ) {
      const pathParts = path.split("/").filter(Boolean);
      const reportCategory = pathParts[1];
      const cleanedReportName = cleanReportName(selectedReportName);
      return `Reports / ${reportCategory
        .replace(/-/g, " ")
        .replace(/\b\w/g, (l) => l.toUpperCase())} / ${cleanedReportName}`;
    }
    if (path === "/profile" && location.state?.editMode) {
      return "Profile / Edit Company Profile";
    }
    return path
      .split("/")
      .filter(Boolean)
      .map((part) =>
        part.replace(/-/g, " ").replace(/\b\w/g, (l) => l.toUpperCase()),
      )
      .join(" / ");
  };

  const subtitle = useMemo(() => {
    const firstSegment = location.pathname.split("/").filter(Boolean)[0];
    if (isHome) return "Welcome back to your workspace";
    if (location.pathname === "/profile" && location.state?.editMode) return "";
    return TAB_SUBTITLES[firstSegment] || "";
  }, [location.pathname, location.state, isHome]);

  const initials = useMemo(() => {
    try {
      const username = localStorage.getItem("user_name")?.trim();
      if (username) {
        return username.slice(0, 2).toUpperCase();
      }

      const org = JSON.parse(localStorage.getItem("organisation") || "null");
      const name = org?.display_name || org?.name || "";
      const parts = name.trim().split(/\s+/).filter(Boolean);
      if (parts.length >= 2) {
        return (parts[0][0] + parts[1][0]).toUpperCase();
      }
      if (parts.length === 1 && parts[0].length >= 2) {
        return parts[0].slice(0, 2).toUpperCase();
      }
    } catch (e) {
    }
    return "FP";
  }, []);

  return (
    <header className="header">
      <div className="header-left">
        {!isHome && (
          <button
            className="header-back"
            onClick={() => navigate(-1)}
            aria-label="Go back"
          >
            <ChevronLeft size={20} />
          </button>
        )}
        <div className="header-titles">
          <span className="header-title" title={location.pathname}>
            {formatPath(location.pathname)}
          </span>
          {subtitle && <span className="header-subtitle">{subtitle}</span>}
        </div>
      </div>

      <div className="header-right">
        <MinimizePageButton className="header-icon-btn" />
        <button
          className={`header-icon-btn ${assistant?.open || location.pathname === "/assistant" ? "active" : ""}`}
          onClick={() => assistant?.open || location.pathname === "/assistant" ? assistant?.minimize() : assistant?.restore()}
          aria-label="Open Udyo assistant"
          title="Udyo assistant"
        >
          <Sparkles size={20} />
        </button>
        <button
          className="header-icon-btn"
          onClick={toggleTheme}
          aria-label={isDark ? "Switch to light mode" : "Switch to dark mode"}
          title={isDark ? "Light mode" : "Dark mode"}
        >
          {isDark ? <Sun size={20} /> : <Moon size={20} />}
        </button>

        <div className="header-notifications">
          <button
            ref={bellRef}
            className={`header-icon-btn ${panelOpen ? "active" : ""}`}
            onClick={() => setPanelOpen((open) => !open)}
            aria-label="Notifications"
            aria-expanded={panelOpen}
            title="Notifications"
          >
            <Bell size={20} />
            {unreadCount > 0 && (
              <span className="notification-badge">
                {unreadCount > 9 ? "9+" : unreadCount}
              </span>
            )}
          </button>

          {panelOpen && (
            <NotificationsPanel
              anchorRef={bellRef}
              onClose={() => setPanelOpen(false)}
            />
          )}
        </div>

        <button
          className={`header-icon-btn ${isTasksActive ? "active" : ""}`}
          onClick={() => navigate("/tasks")}
          aria-label="Tasks"
          title="Tasks"
        >
          <ListTodo size={20} />
        </button>

        <button
          className={`header-avatar ${isProfileActive ? "active" : ""}`}
          onClick={() => navigate("/profile")}
          aria-label="Profile"
          title="Profile"
        >
          {initials}
        </button>
      </div>
    </header>
  );
};

export default Header;
